import React from 'react';
import { useNavigate } from 'react-router-dom';

interface DashboardMetrics {
  healthGrade: string;
  averageScore: number;
  totalFlows: number;
  criticalIssues: number;
  incompleteFlows: number;
}

interface KeyMetricsCardsProps {
  metrics: DashboardMetrics | null;
  selectedOrgId: string | null;
  affectedUsersCount: number;
  onImpactedUsersClick?: () => void;
  onCriticalIssuesClick?: () => void;
}

// Grade colors
const GRADE_COLORS: Record<string, string> = {
  'A+': '#10B981', 'A': '#10B981', 'A-': '#10B981',  // green
  'B+': '#3B82F6', 'B': '#3B82F6', 'B-': '#3B82F6',  // blue
  'C+': '#F59E0B', 'C': '#F59E0B', 'C-': '#F59E0B',  // amber
  'D+': '#F97316', 'D': '#F97316', 'D-': '#F97316',  // orange
  'F': '#6B7280'  // gray
};

export function KeyMetricsCards({ metrics, selectedOrgId, affectedUsersCount, onImpactedUsersClick, onCriticalIssuesClick }: KeyMetricsCardsProps) {
  const navigate = useNavigate();
  
  if (!metrics) return null;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Health Grade */}
      <div 
        className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => navigate(`/${selectedOrgId}/flows`)}
        title="View all flows"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Health Grade</p>
            <p className="text-3xl font-bold mt-2" style={{ color: GRADE_COLORS[metrics.healthGrade] }}>
              {metrics.healthGrade === 'F' ? 'I' : metrics.healthGrade}
            </p>
          </div>
          <div className="h-12 w-12 rounded-full flex items-center justify-center" 
               style={{ backgroundColor: `${GRADE_COLORS[metrics.healthGrade]}20` }}>
            <span className="text-2xl">📊</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Ave. Score {metrics.averageScore}% over {metrics.totalFlows} flows
        </p>
      </div>

      {/* Critical Issues */}
      <div 
        className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => onCriticalIssuesClick ? onCriticalIssuesClick() : navigate(`/${selectedOrgId}/flows?filter=critical&context=metrics`)}
        title={onCriticalIssuesClick ? "View critical issues breakdown" : "View flows with critical issues"}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Critical Issues</p>
            <p className="text-3xl font-bold text-red-600 mt-2">{metrics.criticalIssues}</p>
          </div>
          <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">⚠️</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">High severity issue types</p>
      </div>

      {/* Incomplete Flows */}
      <div 
        className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => navigate(`/${selectedOrgId}/flows?grade=I&context=metrics`)}
        title="View incomplete flows"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Incomplete Flows</p>
            <p className="text-3xl font-bold text-amber-600 mt-2">{metrics.incompleteFlows}</p>
          </div>
          <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">📝</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">Low scoring flows</p>
      </div>

      {/* Impacted Users */}
      <div 
        className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => onImpactedUsersClick ? onImpactedUsersClick() : navigate(`/${selectedOrgId}/flows?filter=impactedUsers&context=metrics`)}
        title={onImpactedUsersClick ? "View most affected users" : "View flows impacting users"}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Impacted Users</p>
            <p className="text-3xl font-bold text-purple-600 mt-2">{affectedUsersCount}</p>
          </div>
          <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">👥</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">With flow error interviews</p>
      </div>
    </div>
  );
}